import { useEffect, useState } from 'react';
import { connect, RootStateOrAny } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { useHistory } from 'react-router';

import { Typography, Breadcrumb } from 'antd';
import { HomeOutlined, UserOutlined, EditOutlined } from '@ant-design/icons';

import { ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';

import { getClaimReadWrite } from 'store/claim/providerList/actions';
import { search } from 'store/claim/member/actions';
import { save } from 'store/claim/actions';
import { getHelpCenterLinks } from 'store/app/helpCenter/actions';

import SearchMemberForm from 'components/Claims/CreateClaim/SearchMemberForm';
import SearchMemberResultTable from 'components/Claims/CreateClaim/SearchMemberResultTable';
import CreateClaimDetail from 'components/Claims/CreateClaim/CreateClaimDetail';
import ClaimConfirmation from 'components/Claims/CreateClaim/ClaimConfirmation';
import HelpCenterLink from 'components/Common/HelpCenterLink';

import styles from './CreateClaim.module.scss';

const { Title } = Typography;

interface CreateClaimPageProps {
  getClaimReadWrite(userId: string): any,
  isFetchingClaimReadWrite: boolean,
  search(searchObject: Object): any,
  isSearching: boolean,
  save(claim: any): any,
  isSavingClaim: boolean,
  getHelpCenterLinks(companyId: string, page: string, type: string): any,
}

const CreateClaimPage = (props: CreateClaimPageProps) => {

  const history = useHistory();

  const [page, setPage] = useState('start');
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [claim, setClaim] = useState<any>({});
  const [provider, setProvider] = useState<any>({});
  const [dos, setDos] = useState('');
  const [accessErrors, setAccessErrors] = useState<string[]>([]);
  const [claimConfirmInfo, setClaimConfirmInfo] = useState<any>({});
  const [helpCenter, setHelpCenter] = useState<any>([]);


  useEffect(() => {
    getHelpCenterLinks();
  }, []);

  const getHelpCenterLinks = () => {
    props.getHelpCenterLinks(sessionStorage.getItem('ipaGroup')!, '/claims/create', 'page')
      .then((response: any) => {
        setHelpCenter(response);
    })
    .catch((err: ErrorResponse) => {
      console.log(err?.response?.data?.Message);
    });
  }

  return(
    <div className={styles["create-claims-page"]}>
      <div className={styles["header-title"]}>
        <Title level={4}>&nbsp;&nbsp;&nbsp;Create Claim</Title>
      </div>

      {page === 'start' && (
        <>
        <div>
          <Breadcrumb>
            <Breadcrumb.Item>
              <HomeOutlined
                onClick={() => history.push('/quality/overview')}
              />
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <UserOutlined />
              <a onClick={() => setPage('start')}>Create Claim</a> {/* Can use Link class instead */}
            </Breadcrumb.Item>
            </Breadcrumb>
            {
              helpCenter?.length > 0 ? helpCenter.map((hc: any) => {
                return (
                  <>
                    <div className={`row ${styles['help-center-link']}`}>
                      <div className="col-md-24">
                        <HelpCenterLink
                          clickEventId={hc.ClickEventId}
                          linkName={hc.LinkName}
                          pagePath={hc.PagePath}
                          url={hc.Link}
                          companyId={hc.CompanyId}
                        />
                      </div>
                    </div>
                  </>
                );
              }) : null
            }
        </div>
        <div>
          <div className={styles["search-section"]}>
            <SearchMemberForm
              getClaimReadWrite={props.getClaimReadWrite}
              isFetchingClaimReadWrite={props.isFetchingClaimReadWrite}
              search={props.search}
              isSearching={props.isSearching}
              setDataSource={setDataSource}
              provider={provider}
              setProvider={setProvider}
              dos={dos}
              setDos={setDos}
              setAccessErrors={setAccessErrors}
              accessErrors={accessErrors}
            />
          </div>
          {dataSource.length !== 0 && (
            <div className={styles["content"]}>
              <Title level={4}>Members</Title>
              <SearchMemberResultTable
                dataSource={dataSource}
                setPage={setPage}
                setClaim={setClaim}
                isSearching={props.isSearching}
                setAccessErrors={setAccessErrors}
              />
            </div>
          )}
        </div>
        </>
      )}
      {page === 'claim-detail' && (
        <>
        <div>
          <Breadcrumb>
            <Breadcrumb.Item>
              <HomeOutlined
                onClick={() => history.push('/quality/overview')}
              />
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <UserOutlined />
              <a onClick={() => setPage('start')}>Create Claim</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <EditOutlined />
              <a>Create Claim Detail</a>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <CreateClaimDetail
          claim={claim}
          provider={provider}
          dos={dos}
          setPage={setPage}
          setClaimConfirmInfo={setClaimConfirmInfo}
          save={props.save}
          isSavingClaim={props.isSavingClaim}
        />
        </>
      )}
      {page === 'claim-confirm' && (
        <>
        <div>
          <Breadcrumb>
            <Breadcrumb.Item>
              <HomeOutlined
                onClick={() => history.push('/quality/overview')}
              />
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <UserOutlined />
              <a onClick={() => setPage('start')}>Create Claim</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <EditOutlined />
              <a onClick={() => setPage('claim-detail')}>Create Claim Detail</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <EditOutlined />
              <a onClick={() => setPage('claim-confirm')}>Claim Confirmation</a>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
       <ClaimConfirmation
          claimConfirmInfo={claimConfirmInfo}
          setPage={setPage}
       />
        </>
      )}
    </div>
  );
}

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingClaimReadWrite: store.rootReducer.claimProviderList.isFetchingWrite,
  isSearching: store.rootReducer.claimMember.isSearching,
  isSavingClaim: store.rootReducer.claim.isSaving,
  isCreatingGenProv: store.rootReducer.app.isCreatingGenProv,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
{
  getClaimReadWrite,
  search,
  save,
  getHelpCenterLinks,
},
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(CreateClaimPage);